'use client';

import * as React from 'react';
import PropTypes from 'prop-types';
import { useComponentRenderer } from '../../utils/useComponentRenderer.js';
import { mergeProps } from '../../merge-props/index.js';
import { SelectGroupContext } from './SelectGroupContext.js';
import { jsx as _jsx } from "react/jsx-runtime";
const state = {};

/**
 * Groups related select items with the corresponding label.
 * Renders a `<div>` element.
 *
 * Documentation: [Base UI Select](https://base-ui.com/react/components/select)
 */
const SelectGroup = /*#__PURE__*/React.forwardRef(function SelectGroup(props, forwardedRef) {
  const {
    className,
    render,
    ...otherProps
  } = props;
  const [labelId, setLabelId] = React.useState();
  const getSelectItemGroupProps = React.useCallback((externalProps = {}) => mergeProps({
    role: 'group',
    'aria-labelledby': labelId
  }, externalProps), [labelId]);
  const contextValue = React.useMemo(() => ({
    labelId,
    setLabelId
  }), [labelId, setLabelId]);
  const {
    renderElement
  } = useComponentRenderer({
    propGetter: getSelectItemGroupProps,
    render: render ?? 'div',
    ref: forwardedRef,
    state,
    className,
    extraProps: otherProps
  });
  return /*#__PURE__*/_jsx(SelectGroupContext.Provider, {
    value: contextValue,
    children: renderElement()
  });
});
process.env.NODE_ENV !== "production" ? SelectGroup.propTypes /* remove-proptypes */ = {
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * CSS class applied to the element, or a function that
   * returns a class based on the component’s state.
   */
  className: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * Allows you to replace the component’s HTML element
   * with a different tag, or compose it with another component.
   *
   * Accepts a `ReactElement` or a function that returns the element to render.
   */
  render: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
} : void 0;
export { SelectGroup };